/**
 * Bozzeta — regalo automático de pulsera al llegar a 10 charms.
 *
 * Al alcanzar 10 unidades de "charms" en el carrito (todo lo que no sea una
 * de las pulseras base), se muestra un selector para elegir el color de la
 * pulsera de regalo. Al elegir, se añade esa pulsera al carrito con una
 * propiedad especial para identificarla como regalo. El precio a 0€ lo
 * aplica automáticamente el descuento "Compra 10 charms y recibe una
 * pulsera gratis" (BXGY) ya activo en la tienda — este script solo se
 * encarga de añadir/quitar la pulsera del carrito en el momento adecuado.
 *
 * Si en el futuro se añaden más estilos de pulsera al catálogo, hay que
 * incluir sus variant id en BRACELET_VARIANTS y GIFT_VARIANTS.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

const CHARM_THRESHOLD = 10;
const GIFT_PROPERTY = '_bozzeta_regalo';

// Pulseras base de la tienda (no cuentan como "charms").
const GIFT_VARIANTS = [
  { id: 54425731400017, label: 'Dorada' },
  { id: 54425733038417, label: 'Plateada' },
];
const BRACELET_VARIANT_IDS = new Set(GIFT_VARIANTS.map((v) => v.id));

let busy = false;

function getSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
}

async function getCart() {
  const res = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`No se pudo leer el carrito: ${res.status}`);
  return res.json();
}

function charmQuantity(cart) {
  return cart.items.reduce((sum, item) => (BRACELET_VARIANT_IDS.has(item.variant_id) ? sum : sum + item.quantity), 0);
}

function findGiftLine(cart) {
  return cart.items.find((item) => item.properties && item.properties[GIFT_PROPERTY] === 'true');
}

function dispatchCartUpdate(action, lines, promise) {
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'gift-with-purchase',
      lines,
      promise,
    })
  );
}

async function addGift(variantId) {
  const deferred = CartLinesUpdateEvent.createPromise();
  dispatchCartUpdate('add', [{ merchandiseId: String(variantId), quantity: 1 }], deferred.promise);

  const payload = {
    items: [{ id: Number(variantId), quantity: 1, properties: { [GIFT_PROPERTY]: 'true' } }],
    sections: getSectionIds().join(','),
  };

  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const response = await res.json();
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'gift-with-purchase',
        sections: response.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[gift-with-purchase] Failed to add gift:', error);
  }
}

async function removeGift(lineKey) {
  const deferred = CartLinesUpdateEvent.createPromise();
  dispatchCartUpdate('remove', [{ id: lineKey, quantity: 0 }], deferred.promise);

  const body = JSON.stringify({
    id: lineKey,
    quantity: 0,
    sections: getSectionIds().join(','),
    sections_url: window.location.pathname,
  });

  try {
    const res = await fetch(Theme.routes.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body,
    });
    const response = await res.json();
    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'gift-with-purchase',
        sections: response.sections,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
    console.warn('[gift-with-purchase] Failed to remove gift:', error);
  }
}

function getSummaryEl() {
  return document.querySelector('#cart-drawer .cart-drawer__summary');
}

function removeBanner() {
  document.querySelectorAll('.bz-gift-banner').forEach((el) => el.remove());
}

function showPickerBanner() {
  const summary = getSummaryEl();
  if (!summary) return;

  const existing = summary.previousElementSibling;
  if (existing?.classList?.contains('bz-gift-banner') && !existing.classList.contains('bz-gift-banner--confirmed')) {
    return; // already showing the picker
  }
  removeBanner();

  const banner = document.createElement('div');
  banner.className = 'bz-gift-banner';
  banner.innerHTML = `
    <p class="bz-gift-banner__text">🎁 ¡Has desbloqueado una pulsera gratis! Elige el color:</p>
    <div class="bz-gift-banner__buttons"></div>
  `;
  const buttonsWrap = banner.querySelector('.bz-gift-banner__buttons');
  GIFT_VARIANTS.forEach((variant) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = variant.label;
    btn.dataset.variant = String(variant.id);
    btn.addEventListener('click', async () => {
      if (busy) return;
      busy = true;
      banner.querySelectorAll('button').forEach((b) => (b.disabled = true));
      try {
        const freshCart = await getCart();
        if (!findGiftLine(freshCart)) {
          await addGift(variant.id);
        }
      } finally {
        busy = false;
      }
    });
    buttonsWrap.appendChild(btn);
  });

  summary.insertAdjacentElement('beforebegin', banner);
}

function showConfirmationBanner(giftLine) {
  const summary = getSummaryEl();
  if (!summary) return;

  const existing = summary.previousElementSibling;
  if (existing?.classList?.contains('bz-gift-banner--confirmed')) return;
  removeBanner();

  const colorLabel = GIFT_VARIANTS.find((v) => v.id === giftLine.variant_id)?.label || '';
  const banner = document.createElement('div');
  banner.className = 'bz-gift-banner bz-gift-banner--confirmed';
  banner.innerHTML = `<p class="bz-gift-banner__text">🎁 Regalo añadido: Pulsera ${colorLabel} (gratis)</p>`;
  summary.insertAdjacentElement('beforebegin', banner);
}

async function evaluate() {
  if (busy) return;

  let cart;
  try {
    cart = await getCart();
  } catch (error) {
    console.warn('[gift-with-purchase] Failed to read cart:', error);
    return;
  }

  const qty = charmQuantity(cart);
  const giftLine = findGiftLine(cart);

  if (qty < CHARM_THRESHOLD) {
    removeBanner();
    if (giftLine) {
      busy = true;
      try {
        await removeGift(giftLine.key);
      } finally {
        busy = false;
      }
    }
    return;
  }

  if (giftLine) {
    showConfirmationBanner(giftLine);
  } else {
    showPickerBanner();
  }
}

document.addEventListener(StandardEvents.cartLinesUpdate, () => {
  setTimeout(evaluate, 50);
});

document.addEventListener('DOMContentLoaded', evaluate);

customElements.whenDefined('theme-drawer').then(() => {
  document.querySelector('#cart-drawer')?.addEventListener(DrawerOpenEvent.eventName, evaluate);
});
